/* eslint-disable no-console */
import {
  ActionCodeOperation,
  Auth,
  checkActionCode,
  confirmPasswordReset,
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
} from '@firebase/auth';
import { FirebaseError } from '@firebase/util';

import { app } from './firebase';

// auth object
export const auth: Auth = getAuth(app);

export enum AuthStatus {
  UNAUTHENTICATED = 'UNAUTHENTICATED', // no active health proffessional
  UNVERIFIED = 'UNVERIFIED', // fireface unverified needs phonecode
  VERIFIED = 'VERIFIED', // firebase verified + phonecode
  UNAUTHORIZED = 'UNAUTHORIZED', // no organisation access
  AUTHORIZED = 'AUTHORIZED', // full access to data check pincode
  PINCODE = 'PINCODE',
  // AUTHENTICATED = "AUTHENTICATED", equal to verified
}

export enum PasswordStatus {
  SUCCESS = 'SUCCESS',
  USERNOTFOUND = 'USERNOTFOUND',
}

export interface IVerification {
  phoneNumber: string;
  verificationId: string;
  resolver: MultiFactorResolver;
}

export interface IAuthenticationStatus {
  status: AuthStatus;
  message?: string;
  token?: string;
  verification?: IVerification;
  path?: string;
}

export interface IPasswordStatus {
  success: boolean;
  message?: string;
}

export interface IPinCodeStatus {
  success: boolean;
  message?: string;
}

// change password if user is available
export const changePassword = async (
  newPassword: string
): Promise<IPasswordStatus> => {
  if (auth.currentUser) {
    await updatePassword(auth.currentUser, newPassword);
    return { success: true };
  }
  // user not found TODO
  return { success: false, message: 'user not found' };
};

// send reset password email
export const resetPasswordWithEmail = async (
  email: string
): Promise<IPasswordStatus> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    // "auth/user-not-found"
    const message =
      error instanceof FirebaseError
        ? error.code
        : (error as Error).message ?? 'error';

    return { success: false, message };
  }
};

// reset password with code
export const resetPassword = async (
  resetCode: string,
  password: string,
  confirmPassword: string
): Promise<IPasswordStatus> => {
  if (password !== confirmPassword) {
    // todo message password do not match message
    return { success: false, message: 'passwords do not match' };
  }
  try {
    const actionCodeInfo = await checkActionCode(auth, resetCode);
    if (actionCodeInfo.operation !== ActionCodeOperation.PASSWORD_RESET) {
      return { success: false, message: 'Invalid actioncode type received' };
    }
    await confirmPasswordReset(auth, resetCode, password);
    return { success: true };
  } catch (error) {
    const message =
      error instanceof FirebaseError
        ? error.code
        : (error as Error).message ?? 'error';

    return { success: false, message };
  }
};

// check if role is enrolled with mfa
export const enrolledMFA = () => {
  if (auth.currentUser) {
    const mf = multiFactor(auth.currentUser);
    return mf?.enrolledFactors.length === 0 ? true : !!mf?.enrolledFactors[0];
  }
  return false;
};

export const refreshToken = async () => {
  if (auth.currentUser) {
    return auth.currentUser?.getIdToken(true);
  }

  return null;
};

export const getTokens = async () => {
  let token = localStorage.getItem('token');

  const tokenExpires = localStorage.getItem('expirationTime');

  if (Number(tokenExpires ?? '0') - Date.now() < 30000) {
    token = await refreshToken();
  }

  return {
    token,
    tokenExpires,
  };
};

export const getExpirationTime = (token: string) => {
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  if (decodedToken) {
    const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
    return expirationTime;
  }
  return 0;
};

// verify phone code
export const verifyPhoneCode = async (
  verificationCode: string,
  verification: IVerification
): Promise<IAuthenticationStatus> => {
  const { verificationId, resolver } = verification;

  try {
    // Ask user for the SMS verification code.
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    // Complete sign-in.
    const credential = await resolver.resolveSignIn(multiFactorAssertion);
    const token = await credential.user?.getIdToken();
    return {
      status: AuthStatus.PINCODE, // AuthStatus.VERIFIED,
      token,
    };
  } catch (error) {
    const message =
      error instanceof FirebaseError
        ? error.code
        : (error as Error).message ?? 'error';

    return {
      status: AuthStatus.UNVERIFIED,
      message,
      verification,
    };
  }
};

const checkMFArequired = async (
  error: FirebaseError
): Promise<IAuthenticationStatus> => {
  // multi factor action
  if (error.code === 'auth/multi-factor-auth-required') {
    const resolver = getMultiFactorResolver(auth, error as MultiFactorError);

    // ask user which second factor to use
    if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const { phoneNumber } = resolver.hints[0] as PhoneMultiFactorInfo;

      // Todo fix captcha
      const captcha = document.createElement('div');
      captcha.setAttribute('data-size', 'invisible');
      captcha.setAttribute('style', 'display: none;');
      captcha.className = 'captcha';
      document.body.appendChild(captcha);

      const recaptchaVerifier = new RecaptchaVerifier(auth, captcha, {
        size: 'invisible',
        callback: () => {
          console.log('captcha solved');
        },
        'expired-callback': () => {
          console.log('captcha expired');
        },
      });
      // send sms verification code
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier
      );
      // result with verification
      return {
        status: AuthStatus.UNVERIFIED,
        verification: {
          phoneNumber,
          resolver,
          verificationId,
        },
      };
    }
  }

  return {
    status: AuthStatus.UNAUTHENTICATED,
    message: error.code,
  };
};

// login with email / password
export const loginWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<IAuthenticationStatus> => {
  try {
    // try get credentials based on email / pass
    const credentials = await signInWithEmailAndPassword(auth, email, password);
    const token = await credentials.user.getIdToken();
    return {
      status: AuthStatus.VERIFIED,
      token,
    };
  } catch (error) {
    if (error instanceof FirebaseError) {
      return checkMFArequired(error);
    }

    return {
      status: AuthStatus.UNAUTHENTICATED,
      message: (error as Error).message,
    };
  }
};

// reauthenicate to check if password is correct
export const reauthenticate = async (
  oldPassword: string
): Promise<IAuthenticationStatus> => {
  if (auth.currentUser?.email) {
    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser?.email,
        oldPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      return {
        status: AuthStatus.VERIFIED,
      };
    } catch (error) {
      if (error instanceof FirebaseError) {
        return checkMFArequired(error);
      }
      return {
        status: AuthStatus.UNAUTHENTICATED, // user not found
        message: (error as Error).message,
      };
    }
  }
  return {
    status: AuthStatus.UNAUTHENTICATED, // user not found
    message: 'user not found',
  };
};
